import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const GRADIENTS = [
  "from-[#FF8800]/30 to-[#ff5e00]/10",
  "from-[#00B0FF]/30 to-[#00B0FF]/5",
  "from-[#00E676]/25 to-[#00E676]/5",
];

export default function Team() {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Team | LaunchLab";
    api.get("/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, []);

  return (
    <div data-testid="team-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="team-hero"
        kicker="Leadership & Team"
        title="THE PEOPLE BEHIND"
        highlight="THE ECOSYSTEM."
        subtext="Strategists, designers, engineers and operators building India's next generation of businesses."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {members === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="team-empty">Team profiles will be published soon.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="team-grid">
            {members.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                className="card-beam group rounded-2xl border border-slate-800 bg-[#0D111D]/90 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF8800]/40"
                data-testid={`team-card-${m.id}`}
              >
                <div className={`relative aspect-[4/3] overflow-hidden bg-gradient-to-br ${GRADIENTS[i % 3]} bg-grid-tech`}>
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-display font-extrabold text-6xl text-white/10">
                        {m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl font-bold text-white">{m.name}</h3>
                      <p className="mt-1 text-sm text-[#FF8800]">{m.designation}</p>
                    </div>
                    {m.linkedin && (
                      <a
                        href={m.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${m.name} on LinkedIn`}
                        data-testid={`team-linkedin-${m.id}`}
                        className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-400 hover:text-[#00B0FF] transition-colors shrink-0"
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  {m.bio && <p className="mt-3 text-sm text-slate-400 leading-relaxed">{m.bio}</p>}
                  {m.expertise && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {m.expertise.split(",").filter(Boolean).map((e) => (
                        <span key={e} className="glass px-2.5 py-1 rounded-full text-[10px] font-mono-tech tracking-wider text-slate-300">
                          {e.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
